import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Matches the actual bank_transactions DB table schema
export interface BankTransaction {
  id: string;
  bank_account_id: string;
  transaction_date: string;
  transaction_amount: number;
  transaction_amount_currency: string;
  description_details: string | null;
  transaction_type: string | null;
  transaction_id_reference: string | null;
  end_balance: number | null;
  end_balance_currency: string | null;
  transaction_fee: number | null;
  transaction_fee_currency: string | null;
  import_batch_id: string | null;
  raw_data: Record<string, unknown> | null;
}

export interface BankTransactionFilters {
  bankAccountId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  transactionType?: string;
  page?: number;
  pageSize?: number;
}

export const useBankTransactions = (filters: BankTransactionFilters = {}) => {
  return useQuery({
    queryKey: ['bank-transactions', filters],
    queryFn: async () => {
      const buildQuery = () => {
        let q = supabase
          .from('bank_transactions')
          .select('*')
          .order('transaction_date', { ascending: false });

        if (filters.bankAccountId) q = q.eq('bank_account_id', filters.bankAccountId);
        if (filters.dateFrom) q = q.gte('transaction_date', filters.dateFrom);
        if (filters.dateTo) q = q.lte('transaction_date', filters.dateTo);
        if (filters.search) {
          q = q.or(
            `description_details.ilike.%${filters.search}%,transaction_id_reference.ilike.%${filters.search}%`
          );
        }
        if (filters.transactionType) q = q.eq('transaction_type', filters.transactionType);
        return q;
      };

      // PostgREST caps responses at 1000 rows. Paginate via .range() so the
      // summary cards reflect the true totals for the selected filters.
      const PAGE = 1000;
      const MAX_ROWS = 50000; // hard safety ceiling
      const all: BankTransaction[] = [];
      for (let from = 0; from < MAX_ROWS; from += PAGE) {
        const to = from + PAGE - 1;
        const { data, error } = await buildQuery().range(from, to);
        if (error) throw error;
        const batch = (data ?? []) as unknown as BankTransaction[];
        all.push(...batch);
        if (batch.length < PAGE) break;
      }
      return all;
    },
  });
};

export const useInsertBankTransactions = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (transactions: Array<Record<string, unknown>>) => {
      // Convert raw_data to JSON-compatible format
      const formattedTransactions = transactions.map(tx => ({
        ...tx,
        raw_data: tx.raw_data ? JSON.parse(JSON.stringify(tx.raw_data)) : null,
      }));
      
      const { data, error } = await supabase
        .from('bank_transactions')
        .insert(formattedTransactions as any)
        .select();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to import transactions: ${error.message}`);
    },
  });
};

export const useDeleteBankTransaction = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bank_transactions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      toast.success('Transaction deleted');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete transaction: ${error.message}`);
    },
  });
};
